import { useState, useEffect } from 'react'
import * as Persona from 'persona'
import { get } from 'lodash'
import { IDVStatus } from '../utilities/steps'
import { idvStep, PersonaEvent, PERSONA_ENV } from '../constants'
import {
  fromDoorstepInjectedEnvironment,
  isProduction,
} from '../utilities/environment'

const isProd = isProduction(fromDoorstepInjectedEnvironment(window))

export const usePersonaClient = ({
  email,
  sessionToken,
  referenceId,
  inquiryId,
  templateId,
  updateIdvStatus,
  loadApplication,
  onUpdate,
  trackIdentityVerificationSteps,
  setIsLoading,
  triggerPersonaClientRender,
}) => {
  const [isPersonaClientActive, setIsPersonaClientActive] = useState(
    triggerPersonaClientRender
  )
  useEffect(() => {
    const loadPersona = () => {
      const client = new Persona.Client({
        templateId,
        inquiryId,
        sessionToken,
        environment: isProd ? PERSONA_ENV.Production : PERSONA_ENV.Sandbox,
        referenceId,
        onLoad: () => {
          setIsLoading(false)
          setIsPersonaClientActive(true)
          client.open()
        },
        onStart: async (inquiryId: string) => {
          await updateIdvStatus(referenceId, inquiryId, idvStep.Start, false)
        },
        onComplete: async (inquiryId: string) => {
          setIsLoading(true)
          await updateIdvStatus(referenceId, inquiryId, idvStep.Complete, true)

          // After uploading Dasher document, make sure the data in redux store is up-to-date
          await loadApplication(referenceId)

          setIsLoading(false)
          onUpdate({
            idVerificationStatus: IDVStatus.SUCCESS,
            triggerPersonaClientRender: false,
          })
        },
        onFail: async (inquiryId: string) => {
          setIsLoading(true)
          await updateIdvStatus(referenceId, inquiryId, idvStep.Fail, true)
          setIsLoading(false)
          onUpdate({
            idVerificationStatus: IDVStatus.FAILED,
            triggerPersonaClientRender: false,
          })
        },
        onExit: () => {
          setIsPersonaClientActive(false)
          onUpdate({
            idVerificationStatus: IDVStatus.NOT_COMPLETED,
            triggerPersonaClientRender: false,
          })
        },
        onEvent: (eventName: PersonaEvent, metadata: object) => {
          onUpdate({ triggerPersonaClientRender: false })
          const status = get(metadata, 'status', '')
          trackIdentityVerificationSteps({
            email,
            status,
            step: eventName,
          })
        },
      })
    }
    if (!isPersonaClientActive && triggerPersonaClientRender) {
      loadPersona()
    }
  }, [
    email,
    sessionToken,
    referenceId,
    inquiryId,
    templateId,
    onUpdate,
    updateIdvStatus,
    loadApplication,
    trackIdentityVerificationSteps,
    setIsLoading,
    triggerPersonaClientRender,
    isPersonaClientActive,
  ])
}

export default usePersonaClient
